import * as React from 'react';
import {
  Dialog, DialogContent, Paper, Box, Typography, TextField, Button,
  Alert, IconButton, Menu, MenuItem
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { api } from '../../lib/api';
import { useI18n } from '../../i18n/I18nProvider';
import resetI18n from '../../i18n/pages/auth/reset.i18n';

import { LANGS } from '../../i18n/langs';

const BLUE = '#0466c8';
const BLUE_HOVER = '#004890';

const textFieldSx = {
  '& label.Mui-focused': { color: BLUE },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#ccc' },
    '&:hover fieldset': { borderColor: BLUE },
    '&.Mui-focused fieldset': { borderColor: BLUE },
  },
};

/**
 * Props:
 *  - open
 *  - onClose
 *  - token: string (obligatoire pour reset)
 *  - onResetDone?: () => void
 */
export default function ResetPasswordModal({ open, onClose, token = '', onResetDone }) {
  const { t, register, lang, setLang } = useI18n();
  React.useEffect(() => { register('reset', resetI18n); }, [register]);

  const [langAnchor, setLangAnchor] = React.useState(null);
  const currentLang = LANGS.find(l => l.code === lang) || LANGS[0];

  const [form, setForm] = React.useState({ password: '', confirm: '' });
  const [err, setErr] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if ((form.password || '').length < 6) return setErr(t('reset.errors.len'));
    if (form.password !== form.confirm)   return setErr(t('reset.errors.match'));
    if (!token) return setErr(t('reset.errors.invalid'));

    setLoading(true);
    try {
      await api.post('/auth/reset', { token, password: form.password });
      onResetDone?.();
      onClose?.();
    } catch (e2) {
      setErr(e2?.response?.data?.message || t('reset.errors.invalid'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)' } }}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 0, position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, bgcolor: BLUE }} />
          <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
            <IconButton onClick={(e) => setLangAnchor(e.currentTarget)} size="small" sx={{ p: 0.5, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Box component="img" src={currentLang.flag} alt={currentLang.alt} sx={{ height: 18, width: 'auto', mr: 0.5 }} />
              <ArrowDropDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </IconButton>
            <Menu anchorEl={langAnchor} open={Boolean(langAnchor)} onClose={() => setLangAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              {LANGS.map((l) => (
                <MenuItem key={l.code} onClick={() => { setLang(l.code); setLangAnchor(null); }} selected={l.code === lang} sx={{ minWidth: 48, justifyContent: 'center', py: 0.5 }}>
                  <Box component="img" src={l.flag} alt={l.alt} sx={{ height: 18, width: 'auto' }} />
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 22, md: 26 }, mb: 0.5 }}>{t('reset.title')}</Typography>
          <Typography sx={{ color: 'text.secondary', mb: 2.5 }}>{t('reset.subtitle')}</Typography>

          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

          <Box component="form" onSubmit={onSubmit} noValidate>
            <TextField name="password" label={t('reset.form.password')} type="password" fullWidth required margin="normal" value={form.password} onChange={handleChange} sx={textFieldSx} />
            <TextField name="confirm" label={t('reset.form.confirm')} type="password" fullWidth required margin="normal" value={form.confirm} onChange={handleChange} sx={textFieldSx} />
            <Button type="submit" variant="contained" disabled={loading} fullWidth sx={{ mt: 2, py: 1.25, fontWeight: 700, textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER } }}>
              {loading ? t('reset.form.loading') : t('reset.form.submit')}
            </Button>
          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  );
}
